/* eslint-disable import/prefer-default-export */
import { createRouter as createVueRouter, createWebHistory } from "vue-router";
import { createRouterLayout } from "vue-router-layout";
import { beforeEach } from "@/router/beforeEach.js";
import { afterEach } from "@/router/afterEach.js";
import { isCustomDomain } from "@/util/hostnames.ts";

// Dynamic imports
const BulkCategoryEditor = () => import("@/views/bulkCategoryEditor/index.vue");
const BulkRecordItemsEditor = () =>
  import("@/views/bulkRecordItemsEditor/index.vue");
const Home = () => import("@/views/home/index.vue");
const Categories = () => import("@/views/categories/index.vue");
const Category = () => import("@/views/category/index.vue");
const Person = () => import("@/views/person/index.vue");
const Login = () => import("@/views/Login.vue");
const LinkAccounts = () => import("@/views/LinkAccounts.vue");
const LinkAccountsContinue = () => import("@/views/LinkAccountsContinue.vue");
const Redirect = () => import("@/views/Redirect.vue");
const PostRedirect = () => import("@/views/PostRedirect.vue");
const NotFoundError = () => import("@/views/404.vue");
const RecentlyDeleted = () => import("@/views/recentlyDeleted/index.vue");
const MediaLibraryController = () => import("@/views/mediaLibrary/index.vue");
const CastScreen = () => import("@/views/castScreen/index.vue");
const SettingsSEO = () => import("@/views/settings/seo.vue");
const SettingsGeneral = () => import("@/views/settings/general.vue");
const SettingsAnalytics = () => import("@/views/settings/analytics.vue");
const SettingsDatadogAnalytics = () =>
  import("@/views/settings/datadogAnalytics.vue");
const SettingsCast = () => import("@/views/settings/cast.vue");
const SettingsFonts = () => import("@/views/settings/fonts.vue");
const SettingsUsers = () => import("@/views/settings/users.vue");

const coreRoutes = [
  {
    path: "/home/:styleid",
    name: "Home",
    component: Home,
    meta: { title: "Home" },
  },
  {
    path: "/categories/:styleid",
    name: "Categories",
    component: Categories,
  },
  {
    path: "/subcategories/:styleid/:categoryID",
    name: "SubCategories",
    component: Categories,
  },
  {
    path: "/category/:styleid/:categoryID",
    name: "Category",
    component: Category,
  },
  {
    path: "/entry/:styleid/:categoryID/:entryID",
    name: "Person",
    component: Person,
  },
];

const cmsRoutes = [
  {
    path: "/bulkCategoryEditor/:styleid/:categoryID",
    name: "BulkCategoryEditor",
    component: BulkCategoryEditor,
    meta: { cmsOnly: true },
  },
  {
    path: "/bulkRecordItemsEditor/:styleid/:categoryID/:recordCollectionID",
    name: "BulkRecordItemsEditor",
    component: BulkRecordItemsEditor,
    meta: { cmsOnly: true },
  },
  // settings pages
  {
    path: "/settings/:styleid/users",
    name: "Users",
    component: SettingsUsers,
    meta: { adminOnly: true },
  },
  {
    path: "/settings/:styleid/fonts",
    name: "Fonts",
    component: SettingsFonts,
    meta: { adminOnly: true },
  },
  {
    path: "/settings/:styleid/seo",
    name: "SEO",
    component: SettingsSEO,
    meta: { adminOnly: true },
  },
  {
    path: "/settings/:styleid/analytics",
    name: "Analytics",
    component: SettingsAnalytics,
    meta: { adminOnly: true },
  },
  {
    path: "/settings/:styleid/site-analytics",
    name: "DatadogAnalytics",
    component: SettingsDatadogAnalytics,
    meta: { adminOnly: true },
  },
  {
    path: "/settings/:styleid/rocketcast",
    name: "RocketCast",
    component: SettingsCast,
    meta: { adminOnly: true },
  },
  {
    path: "/settings/:styleid/general",
    name: "General",
    meta: { adminOnly: true },
    component: SettingsGeneral,
  },
  {
    path: "/settings/:styleid",
    redirect: (to) => `/settings/${to.params.styleid}/users`,
  },
  {
    path: "/recentlyDeleted/:styleid",
    name: "RecentlyDeleted",
    component: RecentlyDeleted,
    meta: { adminOnly: true },
  },
  {
    path: "/mediaLibrary/:styleid",
    name: "MediaLibrary",
    component: MediaLibraryController,
    meta: { adminOnly: true },
  },
];

const authRoutes = [
  {
    path: "/login/:styleid?",
    name: "Login",
    component: Login,
  },
  {
    path: "/post-redirect/:styleid",
    name: "PostRedirect",
    component: PostRedirect,
    meta: { cmsOnly: true },
  },
  {
    path: "/redirect",
    name: "Redirect",
    component: Redirect,
  },
  {
    path: "/auth/link-accounts",
    name: "LinkAccounts",
    component: LinkAccounts,
  },
  {
    path: "/auth/link-accounts/continue",
    name: "LinkAccountsContinue",
    component: LinkAccountsContinue,
  },
];

const castRoutes = [
  {
    path: "/cast-screen/:screenId",
    name: "CastScreen",
    component: CastScreen,
  },
];

// Resolve layouts
const RouterLayout = createRouterLayout((layout) => {
  switch (layout) {
    case "utility":
      return import("@/layouts/utility.vue");
    case "settings":
      return import("@/layouts/settings.vue");
    default:
      return import("@/layouts/app.vue");
  }
});

export function createRouter() {
  const routes = [
    ...coreRoutes,
    ...authRoutes,
    ...cmsRoutes,
    ...castRoutes,
    {
      path: "/",
      redirect: () => {
        if (import.meta.env.DEV) {
          return "/home/45";
        }

        // If the hostname is a custom domain, then redirect to the home page without
        // any site ID param specified in the route (will lookup the correct site ID)
        if (isCustomDomain()) {
          return "/home/default";
        }

        return "/login";
      },
    },
    {
      path: "/:pathMatch(.*)*",
      name: "NotFound404",
      component: NotFoundError,
      beforeEnter: (to, from, next) => {
        if (!import.meta.env.DEV) {
          next("/login");
        } else {
          next();
        }
      },
    },
  ];

  // Add layouts
  const layoutAdded = routes.map((route) => {
    const child = { ...route };
    const { path } = route;
    child.path = "";
    return {
      component: RouterLayout,
      path,
      children: [route],
    };
  });

  const router = createVueRouter({
    routes: layoutAdded,
    history: createWebHistory(),
  });

  router.beforeEach(beforeEach);
  router.afterEach(afterEach);

  return router;
}
